import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["questionKey"];
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { createToObject } from 'pricing-pages-lib/utils/queryParamUtils';
import { PPUIPageHeader } from 'pricing-pages-ui-components/semantic/PageHeader';
import { useCallback, useEffect } from 'react';
import { Redirect } from 'react-router-dom';
import BannerSection from '../components/banners/BannerSection';
import ProductPageSubtitle from '../components/common/ProductPageSubtitle';
import ProductPageTitle from '../components/common/ProductPageTitle';
import FailedLoadBanner from '../components/common/loading/FailedLoadBanner';
import FallbackErrorBanner from '../components/common/loading/FallbackErrorBanner';
import { ERROR_SEVERITY } from '../components/error-boundary/error-severity';
import { useErrorBoundary } from '../components/error-boundary/useErrorBoundary';
import withErrorBoundary from '../components/error-boundary/withErrorBoundary';
import Faq from '../components/faqs/Faq';
import FooterMicrocopy from '../components/faqs/FooterMicrocopy';
import Question from '../components/faqs/Question';
import HubSpotCreditsValuePropositions from '../components/hubspot-credits/HubSpotCreditsValuePropositions';
import LearnAboutCreditsSection from '../components/hubspot-credits/LearnAboutCreditsSection';
import UBBKeyFeatures from '../components/key-features/UBBKeyFeatures';
import HeaderLayoutWrapper from '../components/layout/HeaderLayoutWrapper';
import { HubSpotCreditsTilesContainer } from '../components/merch-card/hubspot-credits/HubSpotCreditsTilesContainer';
import { OWNERSHIP_CHECK_FAILED, PRICING_AND_PURCHASING_FAILED_STATUS } from '../constants/ErrorCauses';
import { FaqsConfig } from '../constants/faqs/FaqsConfig';
import { NavItemEnum } from '../constants/routing/NavItems';
import { useAnchorLinkScroll } from '../hooks/htmlElementHooks';
import { useGlobalLoading } from '../hooks/loadingHooks';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useRedirectIfIneligible } from '../hooks/useRedirectIfIneligible';
import { useAppSelector } from '../reducers';
import { isUngatedForHubSpotCreditsSelector } from '../selectors/GatingSelectors';
import { hasGatesLoadedSelector, hasOnlyOwnershipCheckFailedSelector, hasPricingAndPurchasingFailedSelector } from '../selectors/LoadingStateSelectors';
import { highestProductLevelFromProductInventorySelector, ownedHubSpotCreditsTotalSelector } from '../selectors/ProductInventorySelectors';
import { currentSkuIdForProductSelector } from '../selectors/ProductsSelectors';
import { trackPageView } from '../utils/tracking/trackingEventUtils';
import { useQueryParamTerm, useQueryParamsEffect } from '../utils/urls/appQueryParamUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const HubSpotCreditsPage = () => {
  useAnchorLinkScroll();
  useDocumentTitle('hubspot-credits');
  const navItem = NavItemEnum.HUBSPOT_CREDITS;
  const faqsConfig = FaqsConfig[navItem];
  const isUngatedForHubSpotCredits = useAppSelector(isUngatedForHubSpotCreditsSelector);
  const isAppReady = useGlobalLoading();
  const hasPricingAndPurchasingFailed = useAppSelector(hasPricingAndPurchasingFailedSelector);
  const hasOnlyOwnershipCheckFailed = useAppSelector(hasOnlyOwnershipCheckFailedSelector);
  const hasGatesLoaded = useAppSelector(hasGatesLoadedSelector);
  const ownsAdditionalCredits = useAppSelector(state => {
    if (!isAppReady) return false;
    const ownedQuantity = ownedHubSpotCreditsTotalSelector(state);
    return ownedQuantity > 0;
  });
  const highestProductLevelOwned = useAppSelector(state => {
    if (!isAppReady) {
      return null;
    }
    return highestProductLevelFromProductInventorySelector(state);
  });
  const shouldShowUnlockHubSpotCreditsInfo = highestProductLevelOwned ? ['FREE', 'STARTER'].includes(highestProductLevelOwned) : false;
  const unlockHubSpotCreditsSkuId = useAppSelector(state => {
    if (!isAppReady || !shouldShowUnlockHubSpotCreditsInfo) {
      return null;
    }
    return currentSkuIdForProductSelector(state, {
      apiName: 'service-hub-professional'
    });
  });
  const {
    queryParamTerm
  } = useQueryParamTerm();
  useQueryParamsEffect({
    term: queryParamTerm
  });
  const {
    showBoundary
  } = useErrorBoundary();

  /**
   * On public, there's a race condition where pricing and purchasing has succeeded, but the activeFeatureFlags have not been populated yet (@see PublicMetaDataActions). So sometimes this condition gets evaluated to true and redirects to /suite before we actually confirm if the user is gated for Breeze Intelligence.
   */
  useRedirectIfIneligible({
    condition: useCallback(() => {
      if (!hasGatesLoaded) {
        return;
      }
      return !isUngatedForHubSpotCredits;
    }, [hasGatesLoaded, isUngatedForHubSpotCredits])
  });
  useEffect(() => {
    getUsageTracker().setProperties({
      defaultedTab: '',
      tab: ''
    });
    trackPageView({
      screen: navItem
    });
  }, [navItem]);
  if (hasPricingAndPurchasingFailed) {
    if (hasOnlyOwnershipCheckFailed) {
      showBoundary(new Error(OWNERSHIP_CHECK_FAILED));
    }
    showBoundary(new Error(PRICING_AND_PURCHASING_FAILED_STATUS));
  }
  if (!isAppReady || !hasGatesLoaded) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      minHeight: 200,
      size: "md",
      grow: true
    });
  }
  return /*#__PURE__*/_jsxs(HeaderLayoutWrapper, {
    navItem: navItem,
    navMarkerName: "HUBSPOT_CREDITS_PAGE",
    children: [/*#__PURE__*/_jsx(BannerSection, {
      screen: navItem
    }), /*#__PURE__*/_jsx(FailedLoadBanner, {
      hasFailed: hasPricingAndPurchasingFailed
    }), /*#__PURE__*/_jsx(FallbackErrorBanner, {}), /*#__PURE__*/_jsxs(PPUIPageHeader, {
      children: [/*#__PURE__*/_jsx(ProductPageTitle, {
        nonProductIdentifier: navItem
      }), /*#__PURE__*/_jsx(ProductPageSubtitle, {
        nonProductIdentifier: navItem
      })]
    }), ownsAdditionalCredits ? /*#__PURE__*/_jsx(HubSpotCreditsValuePropositions, {
      screen: navItem
    }) : /*#__PURE__*/_jsx(HubSpotCreditsTilesContainer, {
      screen: navItem,
      hubSpotCreditsFeature: "CUSTOMER_AGENT",
      productApiName: 'hubspot-credits-committed',
      unlockHubSpotCreditsSkuIds: unlockHubSpotCreditsSkuId ? [unlockHubSpotCreditsSkuId] : [],
      shouldShowUnlockHubSpotCreditsInfo: shouldShowUnlockHubSpotCreditsInfo
    }), /*#__PURE__*/_jsx(UBBKeyFeatures, {
      navItem: navItem
    }), /*#__PURE__*/_jsx(LearnAboutCreditsSection, {}), /*#__PURE__*/_jsxs(Faq, {
      children: [faqsConfig.map(_ref => {
        let {
            questionKey
          } = _ref,
          passthroughOptions = _objectWithoutPropertiesLoose(_ref, _excluded);
        return /*#__PURE__*/_jsx(Question, Object.assign({
          questionKey: questionKey
        }, passthroughOptions), questionKey);
      }), /*#__PURE__*/_jsx(FooterMicrocopy, {})]
    })]
  });
};
export default withErrorBoundary(HubSpotCreditsPage, {
  fallback: /*#__PURE__*/_jsx(Redirect, {
    to: createToObject('/suite')
  }),
  errorBoundaryName: 'HubSpotCreditsPage',
  severity: ERROR_SEVERITY.HIGH
});